import { NO_VIEW, isNarrowed, type BoardView, type CompletedFilter } from '@/lib/board-view';
import type { BoardQuery, PlainDate } from '@botguide/shared';
import {
  DONE_STAGES,
  STAGE_LABELS,
  canHoldWork,
  currentWeekStart,
  addDays,
  formatWeekLabel,
  idSchema,
  weekRange,
  weekStartSchema,
} from '@botguide/shared';

import { Button, Field, Icon, Select } from '@/components';
import type { SelectOption } from '@/components';
import { useApplications, useClients, useProjects, useUsers } from '@/lib/queries';
import { BOARD_FILTER_PARAMS } from '@/lib/screens';

/* -------------------------------------------------------------------------- */
/* URL <-> filters                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Read the filters out of the query string.
 *
 * Each parameter is validated on its own rather than the object as a whole, so
 * one mangled id in a pasted link drops that one filter instead of silently
 * unfiltering the board.
 *
 * `blockedOnly` is read as a literal `'true'` and never coerced: the schema
 * uses `z.coerce.boolean()`, and `Boolean('false')` is `true`, so round-
 * tripping the off state through coercion would turn the filter permanently on.
 */
export function parseBoardFilters(params: URLSearchParams): BoardQuery {
  const filters: BoardQuery = {};

  for (const key of BOARD_FILTER_PARAMS) {
    const raw = params.get(key);
    if (raw === null) continue;

    if (key === 'blockedOnly') {
      if (raw === 'true') filters.blockedOnly = true;
      continue;
    }

    if (key === 'plannedWeek') {
      const parsed = weekStartSchema.safeParse(raw);
      if (parsed.success) filters.plannedWeek = parsed.data;
      continue;
    }

    const parsed = idSchema.safeParse(raw);
    if (parsed.success) filters[key] = parsed.data;
  }

  return filters;
}

/** The query string for a set of filters. Absent keys mean "no filter". */
export function boardFiltersToParams(filters: BoardQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.clientId) params.set('clientId', filters.clientId);
  if (filters.applicationId) params.set('applicationId', filters.applicationId);
  if (filters.projectId) params.set('projectId', filters.projectId);
  if (filters.assigneeId) params.set('assigneeId', filters.assigneeId);
  if (filters.plannedWeek) params.set('plannedWeek', filters.plannedWeek);
  if (filters.blockedOnly) params.set('blockedOnly', 'true');
  return params;
}

export function hasActiveFilters(filters: BoardQuery): boolean {
  return BOARD_FILTER_PARAMS.some((key) => filters[key] !== undefined);
}

/* -------------------------------------------------------------------------- */
/* Weeks                                                                      */
/* -------------------------------------------------------------------------- */

/** Four weeks back through eight ahead - the window a sprint board plans in. */
const WEEKS_BEHIND = 4;
const WEEKS_AHEAD = 8;

/**
 * Selectable Mondays, labelled with the shared formatter.
 *
 * A linked board may carry a week from outside the window, so a selected value
 * that is not in the list is folded back in rather than silently dropped.
 */
export function weekOptions(selected?: PlainDate | null): SelectOption[] {
  const thisWeek = currentWeekStart();
  const start = addDays(thisWeek, -7 * WEEKS_BEHIND);
  const weeks = weekRange(start, WEEKS_BEHIND + 1 + WEEKS_AHEAD);

  if (selected && !weeks.includes(selected)) weeks.push(selected);
  weeks.sort();

  return weeks.map((week) => ({
    value: week,
    label: week === thisWeek ? `${formatWeekLabel(week)} · this week` : formatWeekLabel(week),
  }));
}

/* -------------------------------------------------------------------------- */
/* The bar                                                                    */
/* -------------------------------------------------------------------------- */

export interface BoardFiltersProps {
  value: BoardQuery;
  onChange: (next: BoardQuery) => void;
  /**
   * What is being looked at, as opposed to what was fetched.
   *
   * Deliberately not part of `BoardQuery`: search and the completed-stage
   * filter narrow tasks already on screen, cost no round trip, and are not in
   * the URL. See `lib/board-view.ts` for why that line is drawn where it is.
   */
  view: BoardView;
  onViewChange: (next: BoardView) => void;
}

/**
 * The board's filter bar.
 *
 * The values live in the URL, not in this component - the owner passes them
 * down and takes the new set back - so a filtered board is a link somebody can
 * send and a reload keeps what was being looked at.
 */
export function BoardFilters({ value, onChange, view, onViewChange }: BoardFiltersProps) {
  const clients = useClients();
  const applications = useApplications(value.clientId ? { clientId: value.clientId } : {});
  const projects = useProjects({
    ...(value.clientId ? { clientId: value.clientId } : {}),
    ...(value.applicationId ? { applicationId: value.applicationId } : {}),
  });
  const users = useUsers();

  const set = (patch: BoardQuery) => onChange({ ...value, ...patch });

  const optionsFrom = <T extends { id: string; name: string }>(rows: T[] | undefined) =>
    (rows ?? []).map((row) => ({ value: row.id, label: row.name }));

  return (
    <div className="rounded-lg border border-line bg-surface/70 p-3 sm:p-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Select
          label="Client"
          placeholder="All clients"
          options={optionsFrom(clients.data)}
          value={value.clientId ?? ''}
          onChange={(event) =>
            // Narrowing the client invalidates whatever application and project
            // sat underneath it, so they are dropped rather than left to filter
            // the board down to nothing.
            set({
              clientId: event.target.value || undefined,
              applicationId: undefined,
              projectId: undefined,
            })
          }
        />

        <Select
          label="Application"
          placeholder="All applications"
          options={optionsFrom(applications.data)}
          value={value.applicationId ?? ''}
          onChange={(event) =>
            set({ applicationId: event.target.value || undefined, projectId: undefined })
          }
        />

        <Select
          label="Project"
          placeholder="All projects"
          options={optionsFrom(projects.data)}
          value={value.projectId ?? ''}
          onChange={(event) => set({ projectId: event.target.value || undefined })}
        />

        <Select
          label="Assignee"
          placeholder="Anyone"
          // `canHoldWork` is the one authority on who may hold a task - the
          // same rule `assertAssignee` enforces server-side. Filtering on
          // `role !== 'client'` here would also offer `owner`, a value the
          // API refuses.
          options={optionsFrom((users.data ?? []).filter((user) => canHoldWork(user.role)))}
          value={value.assigneeId ?? ''}
          onChange={(event) => set({ assigneeId: event.target.value || undefined })}
        />

        <Select
          label="Planned week"
          placeholder="Any week"
          options={weekOptions(value.plannedWeek)}
          value={value.plannedWeek ?? ''}
          onChange={(event) => set({ plannedWeek: event.target.value || undefined })}
        />
      </div>

      <div className="mt-3 grid gap-3 border-t border-line pt-3 sm:grid-cols-2">
        <Field
          label="Find"
          type="search"
          placeholder="Task title"
          value={view.search}
          onChange={(event) => onViewChange({ ...view, search: event.target.value })}
        />

        {/*
          Only the Completed lane carries a stage of Pilot or Production, so
          this narrows that column and leaves the other three alone. Options
          are built from DONE_STAGES rather than written out, so renaming a
          stage label reaches here without anybody remembering to.
        */}
        <Select
          label="Completed"
          placeholder="All completed"
          options={DONE_STAGES.map((stage) => ({ value: stage, label: STAGE_LABELS[stage] }))}
          value={view.completed === 'all' ? '' : view.completed}
          onChange={(event) =>
            onViewChange({ ...view, completed: (event.target.value || 'all') as CompletedFilter })
          }
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-line pt-3">
        <label className="inline-flex cursor-pointer select-none items-center gap-2 text-[0.8125rem] font-medium">
          <input
            type="checkbox"
            checked={value.blockedOnly ?? false}
            onChange={(event) => set({ blockedOnly: event.target.checked || undefined })}
            className="size-4 rounded-xs border-line-strong accent-[var(--danger)]"
          />
          <span className="inline-flex items-center gap-1.5">
            <Icon name="alert" size={13} className="text-danger-ink" />
            Blocked only
          </span>
        </label>

        {hasActiveFilters(value) || isNarrowed(view) ? (
          <Button
            size="sm"
            variant="ghost"
            iconLeft="close"
            className="ml-auto"
            onClick={() => {
              onChange({});
              onViewChange(NO_VIEW);
            }}
          >
            Clear filters
          </Button>
        ) : null}
      </div>
    </div>
  );
}
